import { useState, type ReactNode } from 'react';
import { Field, Select } from '@paige/ui';
import { ActionDialog, TextInput } from './ActionDialog';
import { AccountPicker } from './AccountPicker';
import type { AccountHit, ActionState, EnterpriseQuote, Interval, QuoteInput } from './pb-types';
import { day, dollars, perPage } from './pb-words';

export interface QuoteDialogProps extends ActionState {
  company: string;
  /** The paying account it is billed to now, chosen already; null to pick one. */
  payer: AccountHit | null;
  /** The quote in force, which a new one replaces when it is taken. */
  current: EnterpriseQuote | null;
  search: (q: string) => Promise<AccountHit[]>;
  /** Today, yyyy-MM-dd: the default valid-until is 30 days on. */
  today: string;
  onConfirm: (input: QuoteInput) => void;
  onClose: () => void;
}

interface Terms {
  pages: string;
  price: string;
  interval: Interval;
  packRate: string;
  validUntil: string;
  ref: string;
  note: string;
}

const plus30 = (today: string): string =>
  new Date(Date.parse(`${today}T00:00:00Z`) + 30 * 86_400_000).toISOString().slice(0, 10);

const num = (s: string): number => (s.trim() === '' ? NaN : Number(s));

/** Whether the typed terms are whole: pages 1 to 10,000,000, a price above 0, a pack rate above 0 or none, a last day. */
function whole(t: Terms): boolean {
  const pages = num(t.pages);
  const rate = t.packRate.trim() === '' ? 1 : num(t.packRate);
  const okPages = Number.isInteger(pages) && pages >= 1 && pages <= 10_000_000;
  return okPages && num(t.price) > 0 && rate > 0 && t.validUntil !== '';
}

/** The terms as the API takes them, or null while one is missing or out of range. */
function inputOf(payer: AccountHit | null, t: Terms): QuoteInput | null {
  if (!payer || !whole(t)) return null;
  return {
    zohoAccountId: payer.zohoAccountId,
    ...{ pagesPerMonth: num(t.pages), pricePerPeriod: Math.round(num(t.price) * 100) / 100 },
    ...{ interval: t.interval, packRatePerPage: t.packRate.trim() === '' ? null : num(t.packRate) },
    ...{ validUntil: t.validUntil, quoteRef: t.ref.trim() || null, note: t.note.trim() || null },
  };
}

/** Record Enterprise quote (ADR-0036, section 6.3): Ademero's agreed terms, never edited; taken by a take-on or plan change. */
export function QuoteDialog(p: QuoteDialogProps): ReactNode {
  const [payer, setPayer] = useState<AccountHit | null>(p.payer);
  const [t, setT] = useState<Terms>({
    ...{ pages: '', price: '', interval: 'year', packRate: '', validUntil: plus30(p.today) },
    ...{ ref: '', note: '' },
  });
  const input = inputOf(payer, t);
  return (
    <ActionDialog
      {...p}
      size="lg"
      reason={false}
      title={`${p.current ? 'Replace' : 'Record'} Enterprise quote for ${p.company}`}
      confirmLabel="Record quote"
      ready={input !== null}
      consequence={<QuoteConsequence input={input} current={p.current} />}
      onConfirm={() => input && p.onConfirm(input)}
    >
      <AccountPicker
        label="Paying account (on the Ademero invoice)"
        value={payer}
        onChange={setPayer}
        search={p.search}
      />
      <QuoteFields t={t} onChange={setT} />
    </ActionDialog>
  );
}

type FieldsProps = { t: Terms; onChange: (f: (was: Terms) => Terms) => void };
const setter = (onChange: FieldsProps['onChange'], k: keyof Terms) => (v: string) =>
  onChange((was) => ({ ...was, [k]: v }));
const MONEY = { type: 'number' as const, inputMode: 'decimal' as const };

function QuoteFields(p: FieldsProps): ReactNode {
  return (
    <>
      <div className="pb-grid2">
        <PriceFields {...p} />
        <TermFields {...p} />
      </div>
      <TextInput label="Note" value={p.t.note} onChange={setter(p.onChange, 'note')} max={1000} />
    </>
  );
}

function PriceFields({ t, onChange }: FieldsProps): ReactNode {
  return (
    <>
      <TextInput
        label="Pages a month"
        value={t.pages}
        onChange={setter(onChange, 'pages')}
        type="number"
        inputMode="numeric"
        required
      />
      <TextInput
        label={`Price a ${t.interval} ($)`}
        value={t.price}
        onChange={setter(onChange, 'price')}
        step="0.01"
        required
        {...MONEY}
      />
      <IntervalSelect
        value={t.interval}
        onChange={(v) => onChange((was) => ({ ...was, interval: v }))}
      />
    </>
  );
}

function TermFields({ t, onChange }: FieldsProps): ReactNode {
  return (
    <>
      <TextInput
        label="Pack rate ($ a page)"
        hint="Empty: packs are refused"
        value={t.packRate}
        onChange={setter(onChange, 'packRate')}
        step="0.0001"
        {...MONEY}
      />
      <TextInput
        label="Valid until"
        value={t.validUntil}
        onChange={setter(onChange, 'validUntil')}
        type="date"
        required
      />
      <TextInput
        label="Quote reference"
        value={t.ref}
        onChange={setter(onChange, 'ref')}
        max={100}
      />
    </>
  );
}

export function IntervalSelect(p: { value: Interval; onChange: (v: Interval) => void }): ReactNode {
  return (
    <Field label="Billed">
      {({ id }) => (
        <Select id={id} value={p.value} onChange={(e) => p.onChange(e.target.value as Interval)}>
          <option value="month">Monthly</option>
          <option value="year">Yearly</option>
        </Select>
      )}
    </Field>
  );
}

function QuoteConsequence({
  input,
  current,
}: {
  input: QuoteInput | null;
  current: EnterpriseQuote | null;
}) {
  return (
    <>
      {input ? (
        <p>
          <span className="pb-money">{dollars(input.pricePerPeriod)}</span> a {input.interval} for{' '}
          {input.pagesPerMonth.toLocaleString('en-US')} pages a month, no discount;{' '}
          {input.packRatePerPage === null
            ? 'no packs.'
            : `packs at ${perPage(input.packRatePerPage)} a page.`}{' '}
          It can be taken until {day(input.validUntil)}.
        </p>
      ) : (
        <p>Name the paying account, the pages, the price and the last day it can be taken.</p>
      )}
      <p>
        Recording bills nothing. The quote takes effect when the company is taken on with it or its
        plan changed to it: a higher price a period at once, the difference for the rest of the
        period on the open statement; a lower one at the renewal.
        {current ? ` It then replaces ${current.quoteRef ?? 'the quote in force'}.` : ''}
      </p>
    </>
  );
}
